/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { DocumentVersionType, PageOptionExclusiveType, PageTypeEnum, PageType } from '../algorithm/types';
import { DocumentVersionFlag } from '../documentVersionFlags/documentVersionFlags';
import { DYNAMED_PAGE_ID, hasDynamedGuide } from '../DynaMed/dynamed';
import {
  COMBINED_GUIDE_PAGE_ID,
  COMBINED_GUIDE_PAGE_NAME,
} from './components/InnerMessage/documentTocGuidePageConstants';

// Create a 'fake' page that is the first page that you see when you view a documentVersion
// in the chat.  This shows tabs for the guides.

export function landingPage(documentVersion: DocumentVersionType | undefined): PageType | undefined {
  if (!documentVersion) return undefined;
  if (!documentVersion?.guidePageIds || documentVersion.guidePageIds.length === 0) {
    if (!documentVersion.algorithmRootPageId && hasDynamedGuide(documentVersion.title)) {
      return {
        pageType: PageTypeEnum.GUIDE,
        order: 0,
        id: DYNAMED_PAGE_ID,
        isRoot: true,
        document_version_id: documentVersion.id,
        name: documentVersion.title,
      };
    }
    // This document version has no guide pages.  So it's algorithm only
    // (or it is empty, in which case we would return undefined).
    // If there's no algorithm we will return undefined and show an error to the user
    return documentVersion.pages[documentVersion?.algorithmRootPageId!] as PageType | undefined;
  }
  const hasTocFlag = documentVersion.flags?.includes(DocumentVersionFlag.toc);
  if (hasTocFlag && !documentVersion.algorithmRootPageId) {
    return {
      document_version_id: documentVersion.id,
      id: COMBINED_GUIDE_PAGE_ID,
      isRoot: true,
      name: COMBINED_GUIDE_PAGE_NAME,
      order: 0,
      pageType: PageTypeEnum.GUIDE,
    };
  }
  const options: PageOptionExclusiveType[] = hasTocFlag
    ? [
        {
          id: COMBINED_GUIDE_PAGE_ID,
          text: COMBINED_GUIDE_PAGE_NAME,
        },
      ]
    : documentVersion.guidePageIds.map((guidePageId) => ({
        id: guidePageId, // no real need for an id
        text: documentVersion.pages[guidePageId]?.name || '',
      }));
  if (documentVersion.algorithmRootPageId) {
    options.push({
      id: documentVersion.algorithmRootPageId, // again, no real need for an id
      text: 'Algorithm',
      link: documentVersion.pages[documentVersion.algorithmRootPageId]?.name || '',
    });
  }
  const page: PageType = {
    pageType: PageTypeEnum.TABS,
    options_exclusive_key: undefined,
    options,
    id: 'LANDING', // a special id for this generated landing page
    document_version_id: documentVersion.id,
    name: 'LANDING',
  };
  return page;
}
